import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/services/api'
import type { User, LoginRequest, RegisterRequest, AuthResponse } from '@/types/auth'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const accessToken = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const isLoading = ref(false)

  const isAuthenticated = computed(() => !!accessToken.value && !!user.value)

  // Initialize auth state from localStorage
  const initializeAuth = () => {
    const storedToken = localStorage.getItem('accessToken')
    const storedRefreshToken = localStorage.getItem('refreshToken')
    const storedUser = localStorage.getItem('user')

    if (storedToken && storedUser) {
      accessToken.value = storedToken
      refreshToken.value = storedRefreshToken
      user.value = JSON.parse(storedUser)
      
      // Set auth header
      api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`
    }
  }

  // Login
  const login = async (credentials: LoginRequest): Promise<boolean> => {
    try {
      isLoading.value = true
      const response = await api.post<{ success: boolean; data: AuthResponse }>('/auth/login', credentials)
      
      const { user: userData, accessToken: token, refreshToken: refresh } = response.data.data
      
      user.value = userData
      accessToken.value = token
      refreshToken.value = refresh

      // Store in localStorage
      localStorage.setItem('accessToken', token)
      localStorage.setItem('refreshToken', refresh)
      localStorage.setItem('user', JSON.stringify(userData))

      // Set auth header
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`

      return true
    } catch (error) {
      console.error('Login error:', error)
      return false
    } finally {
      isLoading.value = false
    }
  }

  // Register
  const register = async (userData: RegisterRequest): Promise<boolean> => {
    try {
      isLoading.value = true
      const response = await api.post<{ success: boolean; data: AuthResponse }>('/auth/register', userData)
      
      const { user: newUser, accessToken: token, refreshToken: refresh } = response.data.data
      
      user.value = newUser
      accessToken.value = token
      refreshToken.value = refresh

      // Store in localStorage
      localStorage.setItem('accessToken', token)
      localStorage.setItem('refreshToken', refresh)
      localStorage.setItem('user', JSON.stringify(newUser))

      // Set auth header
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`

      return true
    } catch (error) {
      console.error('Registration error:', error)
      return false
    } finally {
      isLoading.value = false
    }
  }

  // Logout
  const logout = async () => {
    try {
      if (refreshToken.value) {
        await api.post('/auth/logout', { refreshToken: refreshToken.value })
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Clear state
      user.value = null
      accessToken.value = null
      refreshToken.value = null

      // Clear localStorage
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')

      // Clear auth header
      delete api.defaults.headers.common['Authorization']
    }
  }

  // Refresh token
  const refreshAccessToken = async (): Promise<boolean> => {
    try {
      if (!refreshToken.value) {
        return false
      }

      // Create a direct axios instance to avoid interceptor loops
      const axios = (await import('axios')).default
      const refreshApi = axios.create({
        baseURL: '/api',
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
        },
      })

      const response = await refreshApi.post<{ success: boolean; data: { accessToken: string; refreshToken: string } }>('/auth/refresh', {
        refreshToken: refreshToken.value
      })

      const { accessToken: newToken, refreshToken: newRefreshToken } = response.data.data

      accessToken.value = newToken
      refreshToken.value = newRefreshToken

      // Update localStorage
      localStorage.setItem('accessToken', newToken)
      localStorage.setItem('refreshToken', newRefreshToken)

      // Update auth header
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`

      return true
    } catch (error) {
      console.error('Token refresh error:', error)
      await logout()
      return false
    }
  }

  // Update user profile
  const updateProfile = (updatedUser: User) => {
    user.value = updatedUser
    localStorage.setItem('user', JSON.stringify(updatedUser))
  }

  // Handle Google OAuth callback
  const handleGoogleCallback = (token: string, refresh: string) => {
    // Store tokens
    accessToken.value = token
    refreshToken.value = refresh

    // Store in localStorage
    localStorage.setItem('accessToken', token)
    localStorage.setItem('refreshToken', refresh)

    // Set auth header
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`

    // Fetch user profile
    fetchUserProfile()
  }

  // Fetch user profile
  const fetchUserProfile = async () => {
    try {
      const response = await api.get('/auth/me')
      const userData = response.data.data
      
      user.value = userData
      localStorage.setItem('user', JSON.stringify(userData))
    } catch (error) {
      console.error('Failed to fetch user profile:', error)
    }
  }

  return {
    user,
    accessToken,
    refreshToken,
    isLoading,
    isAuthenticated,
    initializeAuth,
    login,
    register,
    logout,
    refreshAccessToken,
    updateProfile,
    handleGoogleCallback
  }
})
